import { useDraggable } from '@dnd-kit/core';
import { useFormBuilder } from '../context/FormBuilderContext';
import type { FieldTypeDefinition, FieldTypeCategory } from '../fieldTypes/registry';
import { encodeDragId } from '../utils/dnd';

const CATEGORY_LABELS: Record<FieldTypeCategory, string> = {
  input: 'Input',
  formatted: 'Formatted Inputs',
  choice: 'Choice',
  layout: 'Layout',
  advanced: 'Advanced',
};

function PaletteItem({ def }: { def: FieldTypeDefinition }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: encodeDragId({ kind: 'palette', fieldType: def.key }),
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={0}
      // k-card gives this draggable item Kendo's real card chrome (border, radius,
      // shadow) — dnd-kit needs a raw DOM ref, which Kendo's <Card> can't provide
      // directly (it forwards a handle object, not the element), so the class is
      // applied to a plain div instead of using the component.
      className="k-card"
      style={{
        display: 'flex',
        // .k-card defaults to flex-direction: column (it's meant to stack a header over a
        // body) — without this override the icon renders above the label instead of next
        // to it, making each palette item much taller than intended.
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--kendo-spacing-2)',
        padding: 'var(--kendo-spacing-2) var(--kendo-spacing-3)',
        marginBottom: 'var(--kendo-spacing-1)',
        background: isDragging ? 'var(--kendo-color-primary-subtle)' : 'var(--kendo-color-app-surface)',
        cursor: 'grab',
        fontSize: 'var(--kendo-font-size-sm)',
        color: 'var(--kendo-color-on-app-surface)',
        userSelect: 'none',
        opacity: isDragging ? 0.5 : 1,
      }}
      title={`Drag to add a ${def.label} field`}
    >
      <span style={{ fontSize: 'var(--kendo-font-size-lg)', color: 'var(--kendo-color-primary)' }}>{def.icon}</span>
      <span style={{ color: 'var(--kendo-color-primary)' }}>{def.label}</span>
    </div>
  );
}

export function FieldPalette() {
  const { fieldTypeRegistry, isEditable } = useFormBuilder();

  const grouped = Object.values(fieldTypeRegistry).reduce<Record<string, FieldTypeDefinition[]>>((acc, def) => {
    (acc[def.category] ??= []).push(def);
    return acc;
  }, {});

  if (!isEditable) {
    return (
      <div style={{ padding: 'var(--kendo-spacing-4)', fontSize: 'var(--kendo-font-size-sm)', color: 'var(--kendo-color-subtle)' }}>
        This version is read-only. Create a new draft to add fields.
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--kendo-spacing-3)', overflowY: 'auto', height: '100%' }}>
      <div
        style={{
          fontSize: 'var(--kendo-font-size-sm)',
          fontWeight: 'var(--kendo-font-weight-bold)',
          color: 'var(--kendo-color-on-app-surface)',
          marginBottom: 'var(--kendo-spacing-3)',
        }}
      >
        Field Palette
      </div>
      {(Object.keys(CATEGORY_LABELS) as FieldTypeCategory[]).map((cat) =>
        grouped[cat]?.length ? (
          <div key={cat} style={{ marginBottom: 'var(--kendo-spacing-4)' }}>
            <div
              style={{
                fontSize: 'var(--kendo-font-size-xs)',
                fontWeight: 'var(--kendo-font-weight-bold)',
                color: 'var(--kendo-color-subtle)',
                textTransform: 'uppercase',
                marginBottom: 'var(--kendo-spacing-2)',
              }}
            >
              {CATEGORY_LABELS[cat]}
            </div>
            {grouped[cat].map((def) => (
              <PaletteItem key={def.key} def={def} />
            ))}
          </div>
        ) : null,
      )}
    </div>
  );
}
